import { fetchAuthSession, getCurrentUser } from '@aws-amplify/auth';
import config from '../../../../config';

const API = config.API_ADDRESS;

async function getAuthHeaders() {
  try {
    const { tokens } = await fetchAuthSession();
    const user = await getCurrentUser();
    if (tokens?.idToken && user?.username) {
      return {
        Authorization: `Bearer ${tokens.idToken.toString()}`,
        'user-id': user.username,
      };
    }
  } catch {
    // Not authenticated
  }
  return {};
}

async function apiFetch(url, options = {}) {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(url, {
    ...options,
    headers: { ...authHeaders, ...(options.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || `Request failed: ${res.status}`);
    err.response = { status: res.status, data: body };
    throw err;
  }
  return res.json();
}

export function createTriggerAdapter(componentId, instanceId, { preview = false } = {}) {
  let _responseMode = 'FILL_SUBMIT';
  let _instanceId = instanceId || null;
  let _pinnedVersion = null; // captured from first /meta response, sent on all subsequent calls

  /** Build query string with pinned version and optional preview flag. */
  function qs() {
    const parts = [];
    if (preview) parts.push('preview=true');
    if (_pinnedVersion != null) parts.push(`workflowVersion=${_pinnedVersion}`);
    return parts.length ? '?' + parts.join('&') : '';
  }

  // /meta additionally carries `instanceId` so the backend FormRenderService can pivot
  // it to the owning ExecutionContext (FlowSession or AgentDynamicFormInstance) and run
  // PlaceholderPrecedenceService, substituting upstream session values into the returned
  // title/description/fields. Kept separate from qs() because submit/draft/validate/upload
  // identify the instance via the request body and must not receive it as a query param
  // as well (the backend treats body and query as distinct sources).
  function qsForMeta() {
    const parts = [];
    if (preview) parts.push('preview=true');
    if (_pinnedVersion != null) parts.push(`workflowVersion=${_pinnedVersion}`);
    if (_instanceId) parts.push(`instanceId=${encodeURIComponent(_instanceId)}`);
    return parts.length ? '?' + parts.join('&') : '';
  }

  const adapter = {
    storagePrefix: 'w86_ml_grant_',

    get instanceId() {
      return _instanceId;
    },

    get capabilities() {
      return {
        canSaveDraft: true,
        canUpload: true,
        hasServerValidation: true,
        hasProgressStatus: false,
        canRefresh: _responseMode === 'FILL_REEDIT',
        showConfirmationOnSubmit: _responseMode === 'FILL_SUBMIT',
      };
    },

    async bootstrap(grantToken) {
      const headers = {};
      if (grantToken) headers['x-verification-grant-token'] = grantToken;
      const metaUrl = `${API}/component-service/dynamic-form/meta/${componentId}${qsForMeta()}`;
      const meta = await apiFetch(metaUrl, { headers });

      if (meta?.notPublished) {
        return { notPublished: true };
      }

      // Pin to the version returned by the first load
      if (meta?.workflowVersion != null && _pinnedVersion == null) {
        _pinnedVersion = meta.workflowVersion;
      }

      _responseMode = meta?.responseMode || 'FILL_SUBMIT';

      const mode = String(meta?.accessPolicy?.mode || 'PUBLIC').toUpperCase();
      const hasFields = Array.isArray(meta?.fields) && meta.fields.length > 0;
      return {
        requiresAuth: mode === 'AUTHENTICATED_USERS' || mode === 'AUTHENTICATED_SELECTED_USERS',
        requiresEmailVerification: mode === 'EMAIL_MAGIC_LINK' && !hasFields,
        disabled: mode === 'DISABLED',
        meta,
      };
    },

    async loadForm(grantToken) {
      const headers = {};
      if (grantToken) headers['x-verification-grant-token'] = grantToken;
      const metaUrl = `${API}/component-service/dynamic-form/meta/${componentId}${qsForMeta()}`;
      const meta = await apiFetch(metaUrl, { headers });

      if (meta?.notPublished) {
        return { notPublished: true, fields: [], title: '', description: '' };
      }

      // Pin to the version returned by the first load
      if (meta?.workflowVersion != null && _pinnedVersion == null) {
        _pinnedVersion = meta.workflowVersion;
      }

      _responseMode = meta?.responseMode || 'FILL_SUBMIT';

      const form = {
        fields: meta.fields || [],
        title: meta.title,
        description: meta.description,
        formSettings: meta.formSettings,
        responseMode: _responseMode,
        isPreview: meta.isPreview || false,
        workflowVersion: meta.workflowVersion || null,
        publishedAt: meta.publishedAt || null,
        // Flow-entry routing: backend sets these when `componentData.flowEntryId === componentId`
        // AND it could pivot the instanceId to a real ExecutionContext. PublicFormPage consumes
        // them to redirect the respondent into /form-flow/{fsid} instead of rendering the
        // standalone dynamic-form UI. Absent for standalone forms and cold-trigger previews.
        flowEntryId: meta.flowEntryId || null,
        isFlowEntry: meta.isFlowEntry === true,
        projectId: meta.projectId || null,
        formSessionId: meta.formSessionId || null,
        testMode: meta.testMode === true,
        // Option A: when this component IS a flow entry, backend ships an authored
        // stepper so PublicFormPage can render FormFlowShell-style chrome on the
        // stable /dynamic-form/{componentId} URL without bootstrapping a FlowSession.
        // Array of {componentId, status: 'CURRENT'|'PENDING', title?, stepperTitle?}.
        flowStepper: Array.isArray(meta.flowStepper) ? meta.flowStepper : null,
      };

      // Fetch prior answers whenever an instanceId is addressed, regardless of
      // responseMode. FILL_REEDIT needs them so the user can re-edit; VIEW_ONLY
      // needs them so the locked form displays the submitted response. Gating
      // happens separately via deriveReadOnly / deriveCanSubmit.
      if (_instanceId) {
        try {
          const draft = await apiFetch(
            `${API}/component-service/dynamic-form/${componentId}/draft/${_instanceId}${qs()}`,
            { headers }
          );
          if (draft?.hasDraft) {
            if (draft.answers) form.answers = draft.answers;
            // `submitted` drives the Submit vs Resubmit button label.
            form.submitted = !!draft.submitted;
            if (draft.submittedAt) form.submittedAt = draft.submittedAt;
            if (draft.savedAt) form.savedAt = draft.savedAt;
          }
        } catch {
          _instanceId = null;
        }
      }

      return form;
    },

    async saveDraft(answers, grantToken) {
      const headers = { 'Content-Type': 'application/json' };
      if (grantToken) headers['x-verification-grant-token'] = grantToken;
      const result = await apiFetch(
        `${API}/component-service/dynamic-form/${componentId}/draft${qs()}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ answers, instanceId: _instanceId }),
        }
      );
      if (result?.instanceId) {
        _instanceId = result.instanceId;
      }
      return result;
    },

    async validate(answers) {
      return apiFetch(
        `${API}/component-service/dynamic-form/${componentId}/validate${qs()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            answers,
            submitMode: false,
            instanceId: _instanceId || undefined,
          }),
        }
      );
    },

    async submit(answers, grantToken) {
      const result = await apiFetch(
        `${API}/component-service/dynamic-form/${componentId}/submit${qs()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            answers,
            instanceId: _instanceId || undefined,
            verificationGrantToken: grantToken || undefined,
          }),
        }
      );
      // Backend allocates a form instance on first submit so subsequent loads of
      // /dynamic-form/:componentId/:instanceId can re-hydrate the submitted answers.
      if (result?.instanceId) {
        _instanceId = result.instanceId;
      }
      return result;
    },

    async upload(fieldKey, file, grantToken) {
      const formData = new FormData();
      formData.append('file', file);
      if (fieldKey) formData.append('fieldKey', fieldKey);
      const authHeaders = await getAuthHeaders();
      const headers = { ...authHeaders };
      if (grantToken) headers['x-verification-grant-token'] = grantToken;
      const parts = [];
      if (preview) parts.push('preview=true');
      if (_pinnedVersion != null) parts.push(`workflowVersion=${_pinnedVersion}`);
      if (_instanceId) parts.push(`instanceId=${encodeURIComponent(_instanceId)}`);
      const query = parts.length ? '?' + parts.join('&') : '';
      const res = await fetch(
        `${API}/component-service/dynamic-form/${componentId}/upload${query}`,
        { method: 'POST', headers, body: formData }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err = new Error(body.error || 'Upload failed');
        err.response = { status: res.status, data: body };
        throw err;
      }
      return res.json();
    },

    async deleteFile(fieldKey, fileId, grantToken) {
      const authHeaders = await getAuthHeaders();
      const headers = { ...authHeaders };
      if (grantToken) headers['x-verification-grant-token'] = grantToken;
      const parts = [];
      if (preview) parts.push('preview=true');
      if (_pinnedVersion != null) parts.push(`workflowVersion=${_pinnedVersion}`);
      if (_instanceId) parts.push(`instanceId=${encodeURIComponent(_instanceId)}`);
      if (fieldKey) parts.push(`fieldKey=${encodeURIComponent(fieldKey)}`);
      const query = parts.length ? '?' + parts.join('&') : '';
      const res = await fetch(
        `${API}/component-service/dynamic-form/${componentId}/upload/${encodeURIComponent(fileId)}${query}`,
        { method: 'DELETE', headers }
      );
      if (!res.ok && res.status !== 204) {
        const body = await res.json().catch(() => ({}));
        const err = new Error(body.error || 'Delete failed');
        err.response = { status: res.status, data: body };
        throw err;
      }
    },

    async requestMagicLink(email) {
      await apiFetch(
        `${API}/component-service/dynamic-form/${componentId}/magic-link/request${qs()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        }
      );
    },

    async verifyMagicLink(verificationToken) {
      return apiFetch(
        `${API}/component-service/dynamic-form/magic-link/verify`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ verificationToken }),
        }
      );
    },

    deriveReadOnly(form) {
      if (!form) return false;
      const rm = form?.responseMode || _responseMode;
      return rm === 'VIEW_ONLY';
    },

    deriveCanSubmit(form) {
      if (!form) return true;
      const rm = form?.responseMode || _responseMode;
      return rm !== 'VIEW_ONLY';
    },

    deriveCanEdit(form) {
      if (!form) return true;
      const rm = form?.responseMode || _responseMode;
      return rm !== 'VIEW_ONLY';
    },
  };

  return adapter;
}
